import { useState } from "react";
import { useProjects } from "@/hooks/useProjects";
import { SharePointTimeline } from "@/components/project/timeline";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import type { TimelineEvent } from "@/types/timeline";

// Generate mock data that showcases all visual elements
function generateMockEvents(): TimelineEvent[] {
  const now = new Date();
  const events: TimelineEvent[] = [];

  // Helper to create a date X days and Y hours ago
  const daysAgo = (days: number, hours: number = 0) => {
    const date = new Date(now);
    date.setDate(date.getDate() - days);
    date.setHours(date.getHours() - hours);
    return date.toISOString();
  };

  // Today - various events
  events.push({
    id: "1",
    file_id: "f1",
    file_name: "Ritning_A101_Plan1.pdf",
    file_path: "/documents/ritningar/Ritning_A101_Plan1.pdf",
    event_type: "created",
    timestamp: daysAgo(0, 1),
    user_first_name: "Erik",
  });

  events.push({
    id: "2",
    file_id: "f2",
    file_name: "Specifikation_El.docx",
    file_path: "/documents/spec/Specifikation_El.docx",
    event_type: "modified",
    timestamp: daysAgo(0, 3),
    user_first_name: "Anna",
  });

  events.push({
    id: "3",
    file_id: "f3",
    file_name: "Kalkyl_2024.xlsx",
    file_path: "/documents/ekonomi/Kalkyl_2024.xlsx",
    event_type: "indexed",
    timestamp: daysAgo(0, 5),
  });

  // Yesterday - multiple edits on same file
  events.push({
    id: "4a",
    file_id: "f4",
    file_name: "Kontrakt_Leverantor.pdf",
    file_path: "/documents/kontrakt/Kontrakt_Leverantor.pdf",
    event_type: "modified",
    timestamp: daysAgo(1, 2),
    user_first_name: "Johan",
  });

  events.push({
    id: "4b",
    file_id: "f4",
    file_name: "Kontrakt_Leverantor.pdf",
    file_path: "/documents/kontrakt/Kontrakt_Leverantor.pdf",
    event_type: "modified",
    timestamp: daysAgo(1, 2.5),
    user_first_name: "Johan",
  });

  events.push({
    id: "4c",
    file_id: "f4",
    file_name: "Kontrakt_Leverantor.pdf",
    file_path: "/documents/kontrakt/Kontrakt_Leverantor.pdf",
    event_type: "created",
    timestamp: daysAgo(1, 3),
    user_first_name: "Maria",
  });

  events.push({
    id: "5",
    file_id: "f5",
    file_name: "corrupt_file.zip",
    file_path: "/uploads/corrupt_file.zip",
    event_type: "failed",
    timestamp: daysAgo(1, 8),
    user_first_name: "Anders",
  });

  // 2 days ago
  events.push({
    id: "6",
    file_id: "f6",
    file_name: "Tidplan_Rev3.mpp",
    file_path: "/documents/projekt/Tidplan_Rev3.mpp",
    event_type: "created",
    timestamp: daysAgo(2, 4),
    user_first_name: "Sofia",
  });

  events.push({
    id: "7",
    file_id: "f7",
    file_name: "Gamla_Ritningar.zip",
    file_path: "/archive/Gamla_Ritningar.zip",
    event_type: "deleted",
    timestamp: daysAgo(2, 10),
    user_first_name: "Erik",
  });

  // 3 days ago - multiple edits
  events.push({
    id: "8a",
    file_id: "f8",
    file_name: "Budget_Q1.xlsx",
    file_path: "/documents/ekonomi/Budget_Q1.xlsx",
    event_type: "indexed",
    timestamp: daysAgo(3, 3),
  });

  events.push({
    id: "8b",
    file_id: "f8",
    file_name: "Budget_Q1.xlsx",
    file_path: "/documents/ekonomi/Budget_Q1.xlsx",
    event_type: "modified",
    timestamp: daysAgo(3, 3.2),
    user_first_name: "Anna",
  });

  events.push({
    id: "8c",
    file_id: "f8",
    file_name: "Budget_Q1.xlsx",
    file_path: "/documents/ekonomi/Budget_Q1.xlsx",
    event_type: "modified",
    timestamp: daysAgo(3, 3.5),
    user_first_name: "Anna",
  });

  events.push({
    id: "8d",
    file_id: "f8",
    file_name: "Budget_Q1.xlsx",
    file_path: "/documents/ekonomi/Budget_Q1.xlsx",
    event_type: "created",
    timestamp: daysAgo(3, 4),
    user_first_name: "Johan",
  });

  // 4 days ago
  events.push({
    id: "9",
    file_id: "f9",
    file_name: "Protokoll_Mote_15.pdf",
    file_path: "/documents/protokoll/Protokoll_Mote_15.pdf",
    event_type: "indexed",
    timestamp: daysAgo(4, 6),
  });

  events.push({
    id: "10",
    file_id: "f10",
    file_name: "Offert_Malare.pdf",
    file_path: "/documents/offerter/Offert_Malare.pdf",
    event_type: "created",
    timestamp: daysAgo(4, 12),
    user_first_name: "Maria",
  });

  // 5 days ago
  events.push({
    id: "11",
    file_id: "f11",
    file_name: "broken_image.png",
    file_path: "/uploads/broken_image.png",
    event_type: "failed",
    timestamp: daysAgo(5, 2),
    user_first_name: "Anders",
  });

  events.push({
    id: "12",
    file_id: "f12",
    file_name: "Rapport_Besiktning.pdf",
    file_path: "/documents/rapporter/Rapport_Besiktning.pdf",
    event_type: "modified",
    timestamp: daysAgo(5, 14),
    user_first_name: "Sofia",
  });

  // 6 days ago
  events.push({
    id: "13",
    file_id: "f13",
    file_name: "Bilaga_A.pdf",
    file_path: "/documents/bilagor/Bilaga_A.pdf",
    event_type: "deleted",
    timestamp: daysAgo(6, 5),
    user_first_name: "Erik",
  });

  events.push({
    id: "14",
    file_id: "f14",
    file_name: "Installationsmanual.pdf",
    file_path: "/documents/manualer/Installationsmanual.pdf",
    event_type: "created",
    timestamp: daysAgo(6, 18),
    user_first_name: "Anna",
  });

  return events;
}

const mockEvents = generateMockEvents();

export default function TimelineTestPage() {
  const { data: projects, isLoading: projectsLoading } = useProjects();
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [useMockData, setUseMockData] = useState(true);

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        <div>
          <h1 className="text-2xl font-bold mb-2">Timeline Test Page</h1>
          <p className="text-muted-foreground">
            Test the SharePoint Timeline component. Toggle mock data to see all visual elements, or
            select a real project to test with live data.
          </p>
        </div>

        <div className="flex items-center gap-8">
          <div className="flex items-center space-x-2">
            <Switch id="mock-mode" checked={useMockData} onCheckedChange={setUseMockData} />
            <Label htmlFor="mock-mode" className="font-medium">
              Show mock data
            </Label>
          </div>

          {!useMockData && (
            <div className="space-y-2">
              <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                <SelectTrigger className="w-80">
                  <SelectValue placeholder="Choose a project..." />
                </SelectTrigger>
                <SelectContent>
                  {projectsLoading ? (
                    <SelectItem value="loading" disabled>
                      Loading projects...
                    </SelectItem>
                  ) : (
                    projects?.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {useMockData ? (
          <div className="border rounded-lg bg-card">
            <SharePointTimeline projectId="mock-project" mockEvents={mockEvents} />
          </div>
        ) : selectedProjectId ? (
          <div className="border rounded-lg bg-card">
            <SharePointTimeline projectId={selectedProjectId} />
          </div>
        ) : (
          <div className="border rounded-lg bg-muted/50 p-12 text-center text-muted-foreground">
            Select a project above to view its timeline
          </div>
        )}

        <div className="text-xs text-muted-foreground space-y-1">
          <p>
            <strong>Test checklist:</strong>
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Timeline shows horizontal axis with date labels (Today, Yesterday, Mon 27...)</li>
            <li>
              Events appear as colored dots (green=created, amber=modified, red=deleted,
              orange=failed, gray=indexed)
            </li>
            <li>Hover over dots to see tooltip with filename, time, event type, and user name</li>
            <li>Click legend items to filter event types (dots appear/disappear)</li>
            <li>Dots spread vertically based on filename</li>
            <li>Switch language to Swedish to verify translations</li>
          </ul>
          {useMockData && (
            <p className="mt-4 text-muted-foreground">
              <strong>Mock data includes:</strong> 18 events across 7 days, all 5 event types, 6
              different users (Erik, Anna, Johan, Maria, Anders, Sofia)
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
